import { ComponentType, lazy, LazyExoticComponent } from "react";
import { enviroment } from "../../enviroment";

// After a deploy the old hashed chunk files are gone; a tab that loaded the
// previous index.html and then navigates to a lazy route gets a
// ChunkLoadError. Without handling, that error lands in the router's
// errorElement (RouteErrorView), whose reset flow clears the cart — a
// destructive answer to a problem a plain reload fixes (the reload fetches
// the fresh index.html/asset-manifest and thus the new chunk URLs).

// One reload attempt per pathname per tab-session. The key is per pathname
// and is deliberately NEVER cleared on success: clearing on success would
// allow a ping-pong loop (chunk A succeeds and re-arms the guard, chunk B
// fails and reloads, forever). With no clearing, reloads are bounded by
// construction at one per pathname per tab.
const RELOAD_KEY_PREFIX = "chunkLoadReloaded:";

export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return (
    error.name === "ChunkLoadError" ||
    // webpack js/css chunk load failures:
    /Loading (CSS )?chunk .* failed/i.test(error.message) ||
    // native dynamic-import failures (Safari/Firefox wording):
    /Failed to fetch dynamically imported module/i.test(error.message) ||
    /Importing a module script failed/i.test(error.message)
  );
}

/**
 * Initiates the one guarded reload for the current pathname. Returns true if
 * the reload was started (the page is about to go away), false if this
 * pathname already used its attempt — or storage is unavailable, in which
 * case reloading would be ungated and is therefore skipped.
 */
export function attemptChunkReload(): boolean {
  const key = RELOAD_KEY_PREFIX + window.location.pathname;
  try {
    if (sessionStorage.getItem(key)) return false;
    sessionStorage.setItem(key, String(Date.now()));
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}

// A lazy component that can also be warmed ahead of the render that needs
// it (admin perf FE-1): `preload()` starts the chunk download and returns the
// SAME promise React.lazy will await later, so the factory (= the webpack
// import()) runs at most once per successful load whichever side asks first.
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors React.lazy's own signature
export type PreloadableLazy<T extends ComponentType<any>> =
  LazyExoticComponent<T> & { preload: () => Promise<unknown> };

/**
 * Drop-in replacement for React.lazy: on a chunk-load failure it performs the
 * one guarded reload above instead of surfacing the error. If the guard is
 * already spent (or the error is not chunk-related), the error propagates to
 * the router's errorElement exactly like plain React.lazy.
 *
 * The returned component carries a static `preload()` (fire-and-forget chunk
 * warming for the menus). With flag adminPerfRouteSuspenseEnabled the factory
 * promise is memoised so preload and the later lazy render share ONE
 * import(); a rejected preload is forgotten, so the real render retries the
 * import and takes the normal ChunkLoadError path above (a transient
 * hover-time failure never poisons the click). Flag off = the historical
 * `lazy(async () => await factory())` path, no memo (React.lazy's own
 * once-only status is the only memo, as before); preload() is then a plain
 * factory() call that the menus never make (they gate on the same flag).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors React.lazy's own signature
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): PreloadableLazy<T> {
  let pending: Promise<{ default: T }> | undefined;
  function load(): Promise<{ default: T }> {
    if (!enviroment.adminPerfRouteSuspenseEnabled) return factory();
    if (!pending) {
      const started = factory();
      pending = started;
      started.catch(() => {
        // Forget only our own attempt (a newer one may already have replaced it).
        if (pending === started) pending = undefined;
      });
    }
    return pending;
  }
  const component = lazy(async () => {
    try {
      return await load();
    } catch (error) {
      if (isChunkLoadError(error) && attemptChunkReload()) {
        // The page is reloading: never settle, so Suspense keeps showing the
        // loading fallback instead of flashing an error view on the way out.
        return new Promise<never>(() => {});
      }
      throw error;
    }
  }) as PreloadableLazy<T>;
  component.preload = () => {
    const p = load();
    // Fire-and-forget: a failed preload is handled (reload/error) by the
    // render that really needs the chunk, never at hover time.
    p.catch(() => undefined);
    return p;
  };
  return component;
}
